import { useEffect } from "react";

/**
 * Has the browser ask before the document unloads while a page holds unsaved edits.
 *
 * A route blocker sees only the navigations the router makes. Reloading, closing the tab and typing
 * an address leave the document itself, and only `beforeunload` is told about those (#7070). The
 * browser writes the prompt; a page can ask for it and cannot word it.
 *
 * The listener is attached only while there is something to lose. A page with a `beforeunload`
 * listener is kept out of the back/forward cache in some browsers, so a clean page attaches none.
 */
export default function useAskBeforeUnload(hasUnsavedEdits: boolean): void {
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const askBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", askBeforeUnload);
    return () => window.removeEventListener("beforeunload", askBeforeUnload);
  }, [hasUnsavedEdits]);
}
